/**
 * Where a new session lands before anyone chooses (RFC 0290 S6).
 *
 * The last project this PERSON started or opened a session in, on this device.
 *
 * NOT THE LAST PROJECT WITH AN ACTIVE SESSION, which is the tempting reading and
 * the wrong one. A background agent finishing a turn somewhere else would move
 * the default between one session and the next, so where your next piece of work
 * lands would depend on what a machine did while you were not looking. The
 * default follows what the person did.
 *
 * DEVICE-LOCAL, in `localStorage`, for the same reason a mount's path is device
 * local (RFC 0290 §3b): it is a fact about one person at one machine and belongs
 * in no synced document. The RFC says "beside the mounts file", which is wrong
 * in one detail worth correcting here rather than silently: this runs in the
 * renderer, which cannot write `~/.mod/desktop/`. Same property, different
 * store.
 */

const RECENT_KEY = 'mod_w_recent_project'

/** The last project worked in on this device, or null when nothing is recorded
 *  or storage is unavailable (a private window, a hardened profile). */
export function readRecentProject(): string | null {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    return raw && raw.length > 0 ? raw : null
  } catch {
    return null
  }
}

/** Record that work happened in a project. Best-effort: failing to remember a
 *  preference must never fail the action that prompted it. */
export function writeRecentProject(projectId: string): void {
  try {
    localStorage.setItem(RECENT_KEY, projectId)
  } catch {
    // No store, no memory. The fallback below still answers.
  }
}

/**
 * The project a new session should land in.
 *
 * GATED ON AVAILABILITY, so a remembered project that no device mounts any more
 * falls back rather than pre-selecting a choice the picker itself would refuse.
 * The pane's own project is the fallback because it is the answer the window
 * gave before this existed, and it is always a real registry.
 *
 * A remembered project absent from `choices` is NOT treated as unavailable. An
 * empty or unsynced World projects as an empty list (RFC 0281 §8), and reading
 * that as "your project is gone" would move a person's work somewhere else
 * every time the World was slow. Absent is not empty, here as everywhere in
 * this pointer chain.
 */
export function defaultProject(
  recent: string | null,
  paneProject: string,
  choices: readonly { projectId: string; disabled: boolean }[],
): string {
  if (!recent) return paneProject
  const choice = choices.find((c) => c.projectId === recent)
  if (choice?.disabled) return paneProject
  return recent
}
